import { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { I18nProvider } from "./context/I18nContext";
import { AdminAuthProvider } from "./context/AdminAuthContext";
import Layout from "./components/Layout";
import Home from "./pages/Home";
import Classics from "./pages/Classics";
import ClassicDetail from "./pages/ClassicDetail";
import Rooms from "./pages/Rooms";
import RoomDetail from "./pages/RoomDetail";
import Approach from "./pages/Approach";
import Contact from "./pages/Contact";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminClassics from "./pages/admin/AdminClassics";
import AdminRooms from "./pages/admin/AdminRooms";
import AdminProjects from "./pages/admin/AdminProjects";
import AdminSettings from "./pages/admin/AdminSettings";
import { Outlet } from "react-router-dom";
import { publicApi } from "./lib/admin-api";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

// Pull settings.accent_color from API and apply as CSS variable on document root
function AccentColorTheme() {
  useEffect(() => {
    (async () => {
      try {
        const r = await publicApi.get("/settings");
        const hex = r?.data?.accent_color;
        if (hex && /^#[0-9a-f]{6}$/i.test(hex)) {
          const hsl = hexToHsl(hex);
          if (hsl) {
            document.documentElement.style.setProperty("--accent", hsl);
            document.documentElement.style.setProperty("--ring", hsl);
          }
        }
      } catch (_e) { /* keep default */ }
    })();
  }, []);
  return null;
}

function hexToHsl(hex) {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0; const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)); break;
      case g: h = ((b - r) / d + 2); break;
      default: h = ((r - g) / d + 4);
    }
    h *= 60;
  }
  return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

export default function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <I18nProvider>
          <BrowserRouter>
            <ScrollToTop />
            <AccentColorTheme />
            <Routes>
              <Route element={<Layout />}>
                <Route path="/" element={<Home />} />
                <Route path="/klassiekers" element={<Classics />} />
                <Route path="/klassiekers/:slug" element={<ClassicDetail />} />
                <Route path="/ruimtes" element={<Rooms />} />
                <Route path="/ruimtes/:slug" element={<RoomDetail />} />
                <Route path="/aanpak" element={<Approach />} />
                <Route path="/contact" element={<Contact />} />
              </Route>

              {/* Admin routes */}
              <Route element={<AdminAuthShell />}>
                <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route element={<AdminLayout />}>
                  <Route path="/admin/dashboard" element={<AdminDashboard />} />
                  <Route path="/admin/classics" element={<AdminClassics />} />
                  <Route path="/admin/rooms" element={<AdminRooms />} />
                  <Route path="/admin/projects" element={<AdminProjects />} />
                  <Route path="/admin/settings" element={<AdminSettings />} />
                </Route>
              </Route>
            </Routes>
          </BrowserRouter>
        </I18nProvider>
      </HelmetProvider>
    </div>
  );
}

function AdminAuthShell() {
  return (
    <AdminAuthProvider>
      <Outlet />
    </AdminAuthProvider>
  );
}
